// src/BuildingList.jsx
import React, { useState, useEffect, useMemo } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import { useNavigate } from "react-router-dom";

const RAW_API_BASE = process.env.REACT_APP_API_BASE || "/api";
const API_BASE = String(RAW_API_BASE).replace(/\/+$/, "");

const STATUS_COLOR_MAP = {
  "Not Started": "#6c757d",
  "Survey": "#0d6efd",
  "Valuation": "#0dcaf0",
  "Negotiation": "#6610f2",
  "Payment": "#fd7e14",
  "Acquired": "#198754",
  "Demolished": "#dc3545"
};

const hexToRgba = (hex, alpha = 0.12) => {
  if (!hex) return `rgba(0,0,0,${alpha})`;
  const h = hex.replace("#", "");
  if (h.length === 3) {
    const r = parseInt(h[0] + h[0], 16);
    const g = parseInt(h[1] + h[1], 16);
    const b = parseInt(h[2] + h[2], 16);
    return `rgba(${r},${g},${b},${alpha})`;
  } else if (h.length === 6) {
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return `rgba(${r},${g},${b},${alpha})`;
  }
  return `rgba(0,0,0,${alpha})`;
};

const formatCurrency = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return "—";
  return `$${n.toLocaleString()}`;
};

const formatArea = (land, bld) => {
  const l = land === 0 ? "0" : (land || null);
  const b = bld === 0 ? "0" : (bld || null);
  if (!l && !b) return "—";
  return `${l ?? "—"} / ${b ?? "—"}`;
};

export default function BuildingList({ onView }) {
  const [currentUser, setCurrentUser] = useState(null); // Store logged-in user
  const [showMyItemsOnly, setShowMyItemsOnly] = useState(false); // Toggle state
  const navigate = useNavigate();

  const [buildings, setBuildings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "id", direction: "asc" });

  const styles = `
    .table-wrapper { background:#fff; padding:16px; border-radius:10px; }
    .controls { display:flex; gap:12px; align-items:center; margin-bottom:12px; }
    .search-input { height:38px; padding:8px 12px 8px 40px; border-radius:8px; border:1px solid #e6e9ec; background:#fff; }
    .search-input::placeholder { color:#9aa4af; }
    .search-icon { position:absolute; left:14px; top:50%; transform:translateY(-50%); color:#9aa4af; }
    .btn-refresh { height:38px; padding:6px 12px; border-radius:8px; border:1px solid #e6e9ec; background:#fff; color:#374151; }
    .custom-table thead th { background:#f5f7f9; color:#6b7280; font-weight:700; font-size:0.78rem; text-transform:uppercase; border-bottom:1px solid #e9edf0; padding:12px 10px; vertical-align:middle; }
    .custom-table tbody td { padding:14px 10px; vertical-align:middle; color:#111827; font-size:0.94rem; }
    .col-id { width:120px; white-space:nowrap; }
    .col-status { width:150px; text-align:center; }
    .col-action { width:110px; text-align:right; }
    .status-pill { display:inline-flex; align-items:center; justify-content:center; gap:8px; min-width:86px; padding:6px 12px; border-radius:999px; font-weight:600; font-size:0.82rem; }
    .address-cell { max-width:540px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; color:#374151; }
    .owner-dot { width:30px; height:30px; border-radius:50%; display:inline-flex; align-items:center; justify-content:center; color:#fff; font-weight:700; margin-right:8px; background:#9ca3af; flex-shrink:0; }
    .action-btn { border-radius:6px; padding:6px 10px; }
    .table-hover tbody tr:hover { background: #fbfcfe; }
    .filter-toggle { font-size: 0.9rem; display: flex; align-items: center; gap: 8px; cursor: pointer; user-select: none; }
    @media (max-width: 900px) { .address-cell { max-width: 240px; } }
  `;

  // 1. Fetch User Info on Mount
  useEffect(() => {
    const fetchUser = async () => {
      const token = localStorage.getItem("token");
      if (!token) return;
      try {
        const res = await fetch(`${API_BASE}/me`, { headers: { "Authorization": `Bearer ${token}` } });
        if (res.ok) setCurrentUser(await res.json());
      } catch (e) { console.error("Failed to fetch user", e); }
    };
    fetchUser();
  }, []);

  // 2. Fetch Locations (Filtered by Org via Backend)
  useEffect(() => {
    let mounted = true;
    const token = localStorage.getItem("token");

    const load = async () => {
      setIsLoading(true);
      setLoadError(null);
      try {
        const res = await fetch(`${API_BASE}/locations`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        if (!res.ok) throw new Error(`Server returned ${res.status}`);
        const rows = await res.json();
        
        const base = (Array.isArray(rows) ? rows : []).map((r = {}, i) => ({
          id: r.building_id || r.uuid || String(i + 1),
          uuid: r.uuid || null,
          x_coord: r.x_coord ?? null,
          y_coord: r.y_coord ?? null,
          status: r.current_status || r.status || "Not Started",
          status_color: r.status_color || STATUS_COLOR_MAP[r.current_status] || null,
          // Capture user emails for filtering
          contact_email: r.email, 
          last_updated_by: r.last_updated_by_email,
          
          address: null, owner: null, land_area: null, building_area: null, value: null, 
          raw: r, raw_detail: null
        }));

        if (!mounted) return;
        setBuildings(base);

        // Fetch details concurrently
        await Promise.all(base.map(async (b) => {
          try {
            if (!b.id) return;
            const r2 = await fetch(`${API_BASE}/location/${encodeURIComponent(b.id)}`, {
              headers: token ? { Authorization: `Bearer ${token}` } : {}
            });
            if (!r2.ok) return;
            const detail = await r2.json();
            const address = detail.address || detail.location_name || "";
            const owner = detail.owner_name || detail.owner || "";
            const land = (detail.land_area === 0) ? 0 : (detail.land_area ?? null);
            const bld = (detail.building_area === 0) ? 0 : (detail.building_area ?? null);
            let value = null;
            if (Array.isArray(detail.history) && detail.history.length) {
              const found = [...detail.history].reverse().find(h => (h.appraised_value !== undefined && h.appraised_value !== null) || (h.final_agreed_price !== undefined && h.final_agreed_price !== null));
              if (found) value = found.appraised_value ?? found.final_agreed_price ?? null;
            }
            if (!mounted) return;
            setBuildings(prev => prev.map(p => {
              if ((p.id && p.id === b.id) || (p.uuid && p.uuid === b.uuid)) {
                return {
                  ...p,
                  address, owner, land_area: land, building_area: bld, value,
                  status: detail.current_status || p.status,
                  status_color: detail.status_color || p.status_color || STATUS_COLOR_MAP[detail.current_status] || p.status_color,
                  raw_detail: detail
                };
              }
              return p;
            }));
          } catch (err) { console.warn("Failed to fetch detail for", b.id, err); }
        }));

      } catch (err) {
        if (mounted) setLoadError(err.message || "Failed to load data");
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    load();
    return () => { mounted = false; };
  }, []);

  const handleSort = (key) => {
    setSortConfig(prev => {
      const direction = prev.key === key && prev.direction === "asc" ? "desc" : "asc";
      return { key, direction };
    });
  };

  const sortedBuildings = useMemo(() => {
    const arr = [...buildings];
    const { key, direction } = sortConfig || {};
    if (!key) return arr;
    arr.sort((a, b) => {
      const A = a[key] ?? "";
      const B = b[key] ?? "";
      const nA = parseFloat(A);
      const nB = parseFloat(B);
      if (!Number.isNaN(nA) && !Number.isNaN(nB)) return direction === "asc" ? nA - nB : nB - nA;
      const sA = String(A).toLowerCase();
      const sB = String(B).toLowerCase();
      if (sA < sB) return direction === "asc" ? -1 : 1;
      if (sA > sB) return direction === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [buildings, sortConfig]);

  // 3. Filter Logic (Search + User Filter)
  const filteredBuildings = useMemo(() => {
    let result = sortedBuildings;

    // A. User Filter (My Items Only)
    if (showMyItemsOnly && currentUser) {
      result = result.filter(item => {
        // Check if I am the contact email OR if I was the last one to update it
        return (item.contact_email === currentUser.email) || 
               (item.last_updated_by === currentUser.email);
      });
    }

    // B. Search Filter
    const s = (searchTerm || "").trim().toLowerCase();
    if (s) {
      result = result.filter(item => {
        return (
          (item.id && String(item.id).toLowerCase().includes(s)) ||
          (item.address && String(item.address).toLowerCase().includes(s)) ||
          (item.owner && String(item.owner).toLowerCase().includes(s)) ||
          (item.raw && item.raw.building_id && String(item.raw.building_id).toLowerCase().includes(s))
        );
      });
    }
    return result;
  }, [sortedBuildings, searchTerm, showMyItemsOnly, currentUser]);

  const renderStatusBadge = (status, color) => {
    const hex = color || STATUS_COLOR_MAP[status] || "#6c757d";
    const bg = hexToRgba(hex, 0.12);
    const style = { backgroundColor: bg, color: hex, border: `1px solid ${hex}33` };
    return <div className="status-pill" style={style}>{status || "Unknown"}</div>;
  };

  return (
    <div className="container-fluid mt-3">
      <style>{styles}</style>
      <div className="table-wrapper shadow-sm">
        <div className="controls justify-content-between">
          <div className="d-flex gap-2">
            <div style={{ position: "relative", minWidth: 300 }}>
              <i className="bi bi-search search-icon"></i>
              <input
                className="form-control search-input"
                placeholder="Search ID, address, or owner..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            {/* My Items Toggle */}
            <div 
              className={`btn btn-refresh d-flex align-items-center ${showMyItemsOnly ? 'bg-primary text-white border-primary' : ''}`}
              onClick={() => setShowMyItemsOnly(!showMyItemsOnly)}
              title="Show only items I created or updated"
              style={{cursor: 'pointer'}}
            >
              <i className={`bi ${showMyItemsOnly ? 'bi-person-fill' : 'bi-person'} me-2`}></i> 
              My Items
            </div>
          </div>

          <button
            className="btn btn-refresh"
            onClick={async () => {
              setBuildings([]); setIsLoading(true); setLoadError(null);
              // Trigger re-mount of effect by forcing a reload (simple way) or extraction function
              window.location.reload(); // Simplest way to ensure clean state refresh for this snippet
            }}
          >
            <i className="bi bi-arrow-clockwise me-1"></i> Refresh
          </button>
        </div>

        <div className="table-responsive">
          <table className="table custom-table table-hover align-middle">
            <thead>
              <tr>
                <th className="col-id" onClick={() => handleSort("id")}>ID <i className="bi bi-arrow-down-up sort-icon"></i></th>
                <th onClick={() => handleSort("address")}>Address <i className="bi bi-arrow-down-up sort-icon"></i></th>
                <th onClick={() => handleSort("owner")}>Owner <i className="bi bi-arrow-down-up sort-icon"></i></th>
                <th className="col-status">Status</th>
                <th onClick={() => handleSort("value")}>Est. Value <i className="bi bi-arrow-down-up sort-icon"></i></th>
                <th onClick={() => handleSort("land_area")}>Area (m²) <i className="bi bi-arrow-down-up sort-icon"></i></th>
                <th className="col-action">Action</th>
              </tr>
            </thead>

            <tbody>
              {isLoading ? (
                <tr><td colSpan="7" className="text-center py-5 text-muted">Loading...</td></tr>
              ) : loadError ? (
                <tr><td colSpan="7" className="text-center py-5 text-danger">{loadError}</td></tr>
              ) : filteredBuildings.length === 0 ? (
                <tr><td colSpan="7" className="text-center py-5 text-muted">No buildings found</td></tr>
              ) : (
                filteredBuildings.map((item) => (
                  <tr key={(item.uuid || item.id) + (item.id || "")}>
                    <td className="fw-bold text-secondary">#{item.id}</td>

                    <td className="address-cell" title={item.address || (item.raw_detail && item.raw_detail.address) || ""}>
                      {item.address || (item.raw_detail && item.raw_detail.address) || <span className="text-muted">—</span>}
                    </td>

                    <td>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <div className="owner-dot" style={{ background: "#9ca3af" }}>
                          {(item.owner || (item.raw_detail && (item.raw_detail.owner_name || item.raw_detail.owner)) || "?").charAt(0)}
                        </div>
                        <div style={{ fontWeight: 600, color: "#374151" }}>
                          {item.owner || (item.raw_detail && (item.raw_detail.owner_name || item.raw_detail.owner)) || "-"}
                        </div>
                      </div>
                    </td>

                    <td className="col-status">
                      {renderStatusBadge(item.status, item.status_color)}
                    </td>

                    <td>{formatCurrency(item.value)}</td>

                    <td>{formatArea(item.land_area ?? (item.raw_detail && item.raw_detail.land_area), item.building_area ?? (item.raw_detail && item.raw_detail.building_area))}</td>

                    <td className="col-action">
                      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                        <button
                        className="btn btn-sm btn-outline-primary action-btn"
                        onClick={() => {
                           navigate("/Map", { state: { focusOn: item } });
                        }}
                      >
                        View
                      </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}